import React, {useCallback, useMemo, useState} from 'react';
import {
    useActiveWorkPackageSelector,
    useWorkPackagesSelector,
    useWorkStreamsSelector
} from "../../../../store/selectors/work-packages.selectors";
import {
    useAllTaskListSelector,
    useCompletedChecklistMapSelector,
    useTaskCodeMapSelector
} from "../../../../store/selectors/task/task.selectors";
import TaskModel from "../../../../models/responses/task.model";
import {WorkPackage, WorkStream} from "../../../../models/responses/work-package.model";
import styles from "./WorkPackages.module.scss";
import {
    useActiveProjectCalendarsSelector,
    useChecklistMapSelector,
    useClientIdSelector,
    useCpmMapSelector, useDagOpenSelector
} from "../../../../store/selectors/project.selectors";
import {convertIndexToSeconds} from "../../../../utils/cpm-functions/cpm-app/functions/handleEvent";
import WorkPackageTaskList from "./components/WorkPackageTaskList";
import {CpmTaskModel} from "../../../../models/responses/cpm-task.model";
import moment from "moment";
import WorkPackageChecklist from "./components/WorkPackageChecklist";
import NewChecklistModal from "../ChecklistPage/components/NewChecklistModal";
import FirebaseUsage from "../../../../firebase/firebase.usage";
import {COLLECTIONS} from "../../../../firebase/constants";
import {Dropdown, Popup} from "semantic-ui-react";
import {useProjectMemberListSelector} from "../../../../store/selectors/team.selectors";
import {MemberOrganizationEnum} from "../../../../models/member-organization.enum";
import {useSearchParamatersSelector} from "../../../../store/selectors/search.selectors";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faChevronDown, faCrown} from "@fortawesome/free-solid-svg-icons";
import {faPen, faChevronUp} from "@fortawesome/pro-solid-svg-icons";
import {useUserGradeSelector} from "../../../../store/selectors/authorization.selectors";
import classNames from "classnames";
import TaskStatusModel from "../../../../models/responses/task-status.model";
import {useDispatch} from "react-redux";
import * as workPackageActions from "../../../../store/actions/work-package.actions"
import * as taskActions from "../../../../store/actions/task.actions"
import UserImage from "../../../../images/UserImage";
import workpackageFilteringUtils from "../../../../utils/workpackage-filtering.utils";
import filterWorkPackages from "../../../../utils/workpackage-filtering.utils";
import NetworkDag from "../../components/DagChart/NetworkDag";
import WorkPackageDag from "../../components/DagChart/WorkPackageDag";

const indentColours = [
    '#dcdada',
    'oklch(60.9% 0.126 221.723)',
    'oklch(78.9% 0.154 211.53)',
    'oklch(91.7% 0.08 205.041)',
    'oklch(98.4% 0.019 200.873)'
]

const indentTextColours = [
    'black',
    'oklch(91.7% 0.08 205.041)',
    '#333333',
    '#333333',
    '#333333',
]

export default function WorkPackages() {
    const workPackages = useWorkPackagesSelector();
    const workStreams = useWorkStreamsSelector();
    const allTasks = useAllTaskListSelector();
    const taskCodeMap = useTaskCodeMapSelector();
    const activeProject = useActiveProjectCalendarsSelector();
    const cpmMap = useCpmMapSelector();
    const calendarMap = useActiveProjectCalendarsSelector();
    const client = useClientIdSelector();
    const searchParams = useSearchParamatersSelector();
    const projectMembers = useProjectMemberListSelector();
    const [openChecklistModal, setOpenChecklistModal] = useState<boolean>(false);
    const [predecessorToggle, setPredecessorToggle] = useState(false);
    const [successorToggle, setSuccessorToggle] = useState(false);
    const checklistMap = useChecklistMapSelector();
    const completedChecklistMap = useCompletedChecklistMapSelector();
    // const [selectedWorkPackage, setSelectedWorkPackage] = useState<null | WorkPackage>(null);
    const selectedWorkPackage = useActiveWorkPackageSelector();
    const [workPackageDescription, setWorkPackageDescription] = useState<string | null>(null);
    const [dagLoaded, setDagLoaded] = useState(false);
    const userGrade = useUserGradeSelector();
    const dagOpen = useDagOpenSelector();
    const dispatch = useDispatch();

    const setSelectedWorkPackage = (workPackage: WorkPackage | null) => {
        dispatch(workPackageActions.Actions.setActiveWorkPackage(workPackage));
    }

    const userIsAdmin = userGrade[MemberOrganizationEnum.SUPER_ACTOR]

    const detailedWorkPackageMap: Map<string, WorkPackage> = useMemo(() => {
        const map = new Map<string, WorkPackage>();
        workPackages.forEach(wp => {
            const tasks: TaskModel[] = wp.assignedTaskCodes
                .map(taskCode => allTasks.get(taskCodeMap.get(taskCode)!))
                .filter((task): task is TaskModel => task !== undefined)
            let packageStart: null | number = null;
            let packageEnd: null | number = null;
            let isCritical = false;
            let isCriticalNow = false;
            let isNearCritical = false;
            let isInProgress = false;
            let drivingTaskStart: null | TaskModel = null;
            let drivingTaskEnd: null | TaskModel = null;
            tasks.forEach(task => {
                const cpmTask: CpmTaskModel = cpmMap.get(task.task_id);
                if (!cpmTask) return;
                const taskStart = convertIndexToSeconds(cpmTask.fs ? cpmTask.fs : cpmTask.es, cpmTask.cal_id, calendarMap);
                const taskEnd = convertIndexToSeconds(cpmTask.ff ? cpmTask.ff : cpmTask.ef, cpmTask.cal_id, calendarMap);
                const workingHours = calendarMap.get(`whd:${cpmTask.cal_id}`);
                if (packageStart === null || taskStart < packageStart) {
                    packageStart = taskStart;
                    drivingTaskStart = task;
                }
                if (packageEnd === null || taskEnd > packageEnd) {
                    packageEnd = taskEnd;
                    drivingTaskEnd = task;
                }
                if (cpmTask.float === 0 || (cpmTask.float && cpmTask.float < workingHours)) {
                    isCritical = true;
                    if (convertIndexToSeconds(cpmTask.ls, cpmTask.cal_id, calendarMap) <=
                        (moment(new Date()).add(1, 'days').toDate().getTime() / 1000)) {
                        isCriticalNow = true;
                    }
                } else if (cpmTask.float && cpmTask.float < workingHours * 2) {
                    isNearCritical = true;
                }
                if (task.status !== TaskStatusModel.NOT_STARTED && task.status !== TaskStatusModel.BLOCK) {
                    isInProgress = true;
                }
            });

            const checklist = checklistMap.get(wp.checklistId);
            let incompleteChecklistItems: any = [];
            if (!checklist) {
                map.set(wp.workPackageRef, {
                ...wp,
                tasks: tasks
                    .sort((a, b) => {
                        const cpmTaskA: CpmTaskModel | undefined = cpmMap.get(a.task_id);
                        const cpmTaskB: CpmTaskModel | undefined = cpmMap.get(b.task_id);
                        if (!cpmTaskA || !cpmTaskB) return 0;
                        const aStart = convertIndexToSeconds(cpmTaskA.ls, cpmTaskA.cal_id, calendarMap);
                        const bStart = convertIndexToSeconds(cpmTaskB.ls, cpmTaskB.cal_id, calendarMap);
                        return aStart - bStart;
                    }),
                checklist: [],
                packageStart,
                packageEnd,
                isCritical,
                isCriticalNow,
                isNearCritical,
                drivingTaskStart,
                drivingTaskEnd,
                isInProgress
            });
                return;
            }

            let earliestDueDate: null | number = null;
            let overdueItemsCount = 0;
            checklist.items.forEach((checklistItem: any) => {
                const completionRecord = completedChecklistMap.get(`${wp.workPackageId}:${checklistItem.checklistItemId}`);
                const cpmTask = drivingTaskStart && drivingTaskEnd ?
                    checklistItem.tMinusDays <= 0 ? cpmMap.get(drivingTaskStart.task_id) : cpmMap.get(drivingTaskEnd.task_id) : null;
                if (!cpmTask) return;
                const workingHours = cpmTask ? calendarMap.get(`whd:${cpmTask.cal_id}`) : 8;
                const dueDate = checklistItem.tMinusDays > 0 ?
                        convertIndexToSeconds((cpmTask.ff ? cpmTask.ff : cpmTask.ef) + (checklistItem.tMinusDays * workingHours * 2), cpmTask.cal_id, calendarMap) :
                        convertIndexToSeconds((cpmTask.fs ? cpmTask.fs : cpmTask.es) + (checklistItem.tMinusDays * workingHours * 2), cpmTask.cal_id, calendarMap)
                if (!completionRecord && dueDate && dueDate < (new Date().getTime() / 1000)) {
                    overdueItemsCount += 1;
                }
                if (earliestDueDate === null || dueDate < earliestDueDate) {
                    earliestDueDate = dueDate;
                }
                incompleteChecklistItems.push({
                    ...checklistItem,
                    type: 'workPackage',
                    due: dueDate,
                    completionRecord: completionRecord,
                })
            });

            map.set(wp.workPackageRef, {
                ...wp,
                tasks: tasks
                    .sort((a, b) => {
                        const cpmTaskA: CpmTaskModel | undefined = cpmMap.get(a.task_id);
                        const cpmTaskB: CpmTaskModel | undefined = cpmMap.get(b.task_id);
                        if (!cpmTaskA || !cpmTaskB) return 0;
                        const aStart = convertIndexToSeconds(cpmTaskA.ls, cpmTaskA.cal_id, calendarMap);
                        const bStart = convertIndexToSeconds(cpmTaskB.ls, cpmTaskB.cal_id, calendarMap);
                        return aStart - bStart;
                    }),
                checklist: incompleteChecklistItems,
                packageStart,
                packageEnd,
                isCritical,
                isCriticalNow,
                isNearCritical,
                drivingTaskStart,
                drivingTaskEnd,
                isInProgress,
                earliestDueDate,
                overdueChecklistItemsCount: overdueItemsCount
            });
        })
        return map;
    }, [workPackages, allTasks, searchParams]);

    const updateWorkPackageDescription = (workPackageId: string) => {
        FirebaseUsage.updateDoc(COLLECTIONS.WORK_PACKAGES, workPackageId, {
            workPackageDescription: workPackageDescription
        }).then(() => {
            setWorkPackageDescription(null);
        })
    }

    const changeWorkPackageOwner = async (workPackageId: string, ownerEmail: string) => {
        await FirebaseUsage.updateDoc(COLLECTIONS.WORK_PACKAGES, workPackageId, {
            workPackageOwner: ownerEmail
        })
    }

    const memberList = useMemo(() => {
        return projectMembers.filter(member =>
            member.grade !== MemberOrganizationEnum.GROUPS && !member.leftProject
        )
            .map(member => ({
                key: member.memberId,
                text: member.userEmail,
                value: member.userEmail
            }));
    }, [projectMembers]);

    const workPackageTag = (wp: WorkPackage) => {
        if (!wp.checklistId) {
            return (
                <div className={styles.NoChecklistTag}>
                    No checklist assigned
                </div>
            )
        }
        if (wp.overdueChecklistItemsCount && wp.overdueChecklistItemsCount > 0) {
            return (
                <div className={classNames(styles.OverdueTag,
                    (selectedWorkPackage && selectedWorkPackage.workPackageId !== wp.workPackageId) ||
                    !selectedWorkPackage ? styles.Blinking : ''
                    )}>
                    {wp.overdueChecklistItemsCount} overdue action{wp.overdueChecklistItemsCount > 1 ? 's' : ''}
                </div>
            )
        } else if (wp.completedChecklist && wp.checklist && wp.completedChecklist.length >= wp.checklist.length) {
            if (wp.isInProgress) {
                return (
                    <div className={styles.InProgressTag}>
                        In progress
                    </div>
                )
            } else {
                return (
                    <div className={styles.ReadyTag}>
                        Ready to start
                    </div>
                )
            }
        } else {
            return (
                <div className={styles.NotReadyTag}>
                    Actions due in {wp.earliestDueDate ? moment.unix(wp.earliestDueDate).fromNow(true) : 'N/A'}
                </div>
            );
        }
        return null;
    }

    const workPackageElement = (wp: WorkPackage | undefined, level: number) => wp && wp.tasks && wp.tasks.length > 0 ? (
        <div className={styles.WorkPackage}
             style={selectedWorkPackage?.workPackageId === wp.workPackageId ? {backgroundColor: '#f0f0f0', cursor: 'auto'} : {}}
             key={wp.workPackageId}
        >
            <div className={styles.InfoPane} onClick={() => {
                setSelectedWorkPackage(selectedWorkPackage && selectedWorkPackage.workPackageId === wp.workPackageId ? null : wp)
                dispatch(taskActions.Actions.setActiveTask(null))
            }}>
                <div className={styles.Titles}>
                    {(selectedWorkPackage && selectedWorkPackage.workPackageId !== wp.workPackageId) || !selectedWorkPackage ?
                    <FontAwesomeIcon
                        icon={faChevronDown}
                        style={{marginRight: '8px'}}
                    /> : null}
                    {wp.workPackageName} ({wp.workPackageRef})
                    {((selectedWorkPackage && selectedWorkPackage.workPackageId !== wp.workPackageId) || !selectedWorkPackage)
                        && wp.workPackageOwner &&
                        <Popup trigger={
                        <div style={{marginRight: "-12px", marginLeft: "8px"}}>
                            <UserImage user={projectMembers.find(member => member.userEmail === wp.workPackageOwner)} width={20} height={20} />
                        </div>} content={wp.workPackageOwner} />}
                    {(selectedWorkPackage && selectedWorkPackage.workPackageId !== wp.workPackageId) ||
                    !selectedWorkPackage ? workPackageTag(wp) : null}
                </div>
                <div className={styles.Details}>
                    {/*<div className={styles.ChecklistProgressContainer}>*/}
                    {/*    {wp.checklistId &&*/}
                    {/*        <div className={styles.ChecklistProgressBar}>*/}
                    {/*            <div*/}
                    {/*                className={styles.ChecklistProgressFill}*/}
                    {/*                style={{*/}
                    {/*                    width: `${wp.checklist && wp.checklist.length > 0 ?*/}
                    {/*                        ((wp.completedChecklist ? wp.completedChecklist.length : 0) / wp.checklist.length) * 100 : 0}%`*/}
                    {/*                }}*/}
                    {/*            ></div>*/}
                    {/*        </div>*/}
                    {/*    }*/}
                    {/*</div>*/}
                    {/*<div className={styles.TaskProgressContainer}>*/}
                    {/*    <div className={styles.TaskProgressBar}>*/}
                    {/*        <div*/}
                    {/*            className={styles.TaskProgressFill}*/}
                    {/*            style={{*/}
                    {/*                width: `${wp.tasks && wp.tasks.length > 0 ?*/}
                    {/*                    (wp.tasks.filter(task => task.status === TaskStatusModel.COMPLETE).length / wp.tasks.length) * 100 : 0}%`*/}
                    {/*            }}*/}
                    {/*        ></div>*/}
                    {/*    </div>*/}
                    {/*</div>*/}
                    <div className={styles.DetailItem}>
                        <span className={styles.DetailLabel}>{
                            wp.packageStart ? moment(new Date(wp.packageStart * 1000)).format('DD-MMM-YY') : 'N/A'}
                        </span>
                    </div>
                    <div className={styles.DetailItem}>
                        <span className={styles.DetailLabel}>{
                            wp.packageEnd ? moment(new Date(wp.packageEnd * 1000)).format('DD-MMM-YY') : 'N/A'}
                        </span>
                    </div>
                    <div className={styles.DetailItem}>
                        <div className={wp.isCritical ? wp.isCriticalNow ?
                            styles.CriticalTag : styles.CriticalPathTag :
                            wp.isNearCritical ? styles.NearCriticalTag :
                            styles.NonCriticalTag
                        }>
                            {wp.isCritical ? wp.isCriticalNow ?  'Critical' : 'Critical Path' :
                                wp.isNearCritical ? 'Near Critical' : ''}
                        </div>
                    </div>
                </div>
            </div>
            {selectedWorkPackage?.workPackageId === wp.workPackageId ? !wp.workPackageDescription || workPackageDescription !== null ?
                <textarea
                    placeholder={workPackageDescription ? undefined : 'Add work package description...'}
                    autoFocus={!dagOpen}
                    value={workPackageDescription || ''}
                    className={styles.WorkPackageDescriptionInput}
                    onChange={(e) => setWorkPackageDescription(e.target.value)}
                    onBlur={() => updateWorkPackageDescription(wp.workPackageId)}
                /> :
                <div className={styles.WorkPackageDescription}>
                    <p>{wp.workPackageDescription}{userIsAdmin &&
                        <FontAwesomeIcon
                            icon={faPen}
                            className={styles.EditPen}
                            onClick={() => setWorkPackageDescription(wp.workPackageDescription || '')}
                        />}</p>
                </div> : null}
            {selectedWorkPackage?.workPackageId === wp.workPackageId ? !wp.workPackageOwner ?
                <Dropdown trigger={
                    <button
                        className={styles.ActionButton}
                    >
                        Assign work package owner
                    </button>}
                          options={memberList}
                          icon={null}
                          scrolling
                          onChange={(_, data) =>
                              data.value && changeWorkPackageOwner(wp.workPackageId, data.value.toString())}
                />
                    :
                <div className={styles.WorkPackageOwner}>
                    <FontAwesomeIcon icon={faCrown} style={{marginRight: "8px"}} />
                    {wp.workPackageOwner}
                    {userIsAdmin && <Dropdown trigger={
                        <FontAwesomeIcon
                            icon={faPen}
                            className={styles.EditPen} />}
                      options={memberList}
                      icon={null}
                      scrolling
                      onChange={(_, data) =>
                          data.value && changeWorkPackageOwner(wp.workPackageId, data.value.toString())}
                    />}
                </div> :
                null}
            {selectedWorkPackage?.workPackageId === wp.workPackageId ? wp.checklist && wp.checklist.length > 0 ?
                <WorkPackageChecklist
                    checklistItems={wp.checklist ? wp.checklist : []}
                    workPackage={wp}
                    infoTag={workPackageTag(wp)}
                /> :
                <button
                    className={styles.ActionButton}
                    onClick={() => setOpenChecklistModal(true)}
                >
                    Assign pre-flight checklist
                </button> : null}
            {selectedWorkPackage?.workPackageId === wp.workPackageId &&
                <WorkPackageTaskList
                    tasks={wp.tasks ? wp.tasks : []}
                    workPackage={wp}
                    predecessorToggle={predecessorToggle}
                    successorToggle={successorToggle}
                    setPredecessorToggle={setPredecessorToggle}
                    setSuccessorToggle={setSuccessorToggle}
                    navigateToTask={() => {}}
                    selectNextTask={() => {}}
                />}
            <div></div>
        </div>
    ) :  null;

    const workStreamElement = (workStream: WorkStream, level: number)=> {
        const workPackages = workStream.children ?
            workStream.children
                .sort((a, b) => {
                    const wpA = detailedWorkPackageMap.get(a);
                    const wpB = detailedWorkPackageMap.get(b);
                    if (!wpA || !wpB) return 0;
                    return wpA.sequenceNumber.localeCompare(wpB.sequenceNumber);
                })
                .filter(wpRef => filterWorkPackages(detailedWorkPackageMap.get(wpRef), searchParams))
                .map(wpRef => workPackageElement(detailedWorkPackageMap.get(wpRef), level + 1)) : [];
        const workPackageCount = workPackages.filter(wp => wp !== null).length;
        if (workPackageCount === 0) {
            return null;
        }
        return (
            <div key={workStream.workStreamId} className={styles.WorkStreamContainer}>
                <div
                    className={styles.WorkStreamHeader}
                    style={{
                        backgroundColor: indentColours[level % indentColours.length],
                        color: indentTextColours[level % indentTextColours.length],
                        cursor: 'pointer'
                    }}
                    onClick={() => dispatch(workPackageActions.Actions.setWorkStreams(workStreams.map(ws => ws.workStreamId === workStream.workStreamId ?
                        {...ws, expanded: !ws.expanded} : ws)))}
                >
                    <FontAwesomeIcon
                        icon={workStream.expanded ? faChevronUp : faChevronDown}
                        style={{marginRight: '8px'}}
                    />
                    <div className={styles.WorkStreamTitle}>{workStream.workStreamName} ({workStream.workStreamRef})</div>
                </div>
                <div className={styles.WorkStreamWorkPackages}>
                    <div
                        className={styles.IndentLine}
                        style={{
                            backgroundColor: indentColours[level % indentColours.length],
                        }}
                    ></div>
                    <div className={styles.WorkPackagesList}>{workStream.expanded && workPackages}</div>
                </div>
            </div>
        )
    }

    const workStreamElements = workStreams
        .sort((a, b) => a.sequenceNumber.localeCompare(b.sequenceNumber))
        .map(ws => workStreamElement(ws, 0));

    return (
        <div className={styles.WorkPackageContainer}
             style={{maxHeight: 'calc(100vh - 160px)', overflowY: 'auto'}}
        >
            {dagOpen && selectedWorkPackage &&
            <WorkPackageDag
                width={window.innerWidth}
                initialHeight={window.innerHeight * 0.3}
                workPackageRef={selectedWorkPackage.workPackageRef}
                delay={false}
                scope={"preds"}
                setShowDag={() => {}}
                setActiveGreenMilestone={() => {}}
                setActiveRedMilestone={() => {}}
                driving={false}
                navigateToTask={() => {}}
                setDagLoaded={setDagLoaded}
                workPackagesMap={detailedWorkPackageMap}
                setSelectedWorkPackage={setSelectedWorkPackage}
            />}
            <div className={styles.WorkPackagesHeader}>
                <div className={styles.HeaderTitle}>Workstream/Work Package ({workPackages.length})</div>
                <div className={styles.RightHeaders}>
                    <div className={styles.RightHeader}>Start</div>
                    <div className={styles.RightHeader}>Finish</div>
                    <div className={styles.RightHeader}>Criticality</div>
                </div>
            </div>
            {workStreamElements}
            {openChecklistModal && selectedWorkPackage &&
                <NewChecklistModal
                    isOpen={openChecklistModal}
                    setOpen={setOpenChecklistModal}
                    clientId={client || ''}
                    workPackageId={selectedWorkPackage.workPackageId}
                    taskId={null}
                    activeChecklistId={selectedWorkPackage.checklistId || null}
            />}
        </div>
    );
}